import { useEffect, useMemo, useRef } from "react"
import { GlobalSidebar, SidebarList, FormInputAntd, AntdButton } from "components"
import { FormProvider, SubmitHandler, useForm } from "react-hook-form"
import { yupResolver } from "@hookform/resolvers/yup"
import { PBTermInitialValues, PBTermSchema } from "Schema"
import { PBTermsInterface } from "@type/payBill.types"
import useAppSelector from "hooks/useAppSelector"
import useAppDispatch from "hooks/useAppDispatch"
import {
	getPBTerms,
	isPBTermLoading,
	isPBTermSaving,
	savePBTerm,
	selectPBTermList,
	selectPBTermById,
} from "store/PayBill/PBTerms.slice"
import { useAdminQueryParams } from "hooks"
import { sortByProperty } from "config/utils"

interface PBTermFormHandler extends PBTermsInterface {}

const PBTerms = () => {
	const dispatch = useAppDispatch()
	const loading = useAppSelector(isPBTermLoading())
	const saving = useAppSelector(isPBTermSaving())
	const PBTermsList = useAppSelector(selectPBTermList)
	const { params, setSearchParams } = useAdminQueryParams()
	const { selected: selectedPBTerm = "" } = params
	const PBTerm = useAppSelector(selectPBTermById(selectedPBTerm))
	const isNewBeingCreated = useRef(false)

	const methods = useForm<PBTermFormHandler>({
		resolver: yupResolver(PBTermSchema),
		defaultValues: PBTermInitialValues,
	})
	const {
		reset,
		handleSubmit,
		formState: { isSubmitting, isSubmitted, isDirty },
	} = methods

	const sidebarPBTerms = useMemo(() => {
		return (PBTermsList ?? []).map(PBTerm => ({ _id: PBTerm._id, name: PBTerm.name }))
	}, [PBTermsList?.length, isSubmitted, saving])

	const onSubmit: SubmitHandler<PBTermFormHandler> = data => {
		isNewBeingCreated.current = true
		dispatch(
			savePBTerm(data, id => {
				setSearchParams({ selected: id })
			}),
		)
	}

	useEffect(() => {
		dispatch(getPBTerms())
	}, [])

	useEffect(() => {
		if (!selectedPBTerm && !!PBTermsList?.length && !isNewBeingCreated.current) {
			setSearchParams({ selected: PBTermsList[0]._id })
		}
	}, [params, PBTermsList])

	useEffect(() => {
		if (selectedPBTerm && PBTerm) {
			reset({ ...PBTermInitialValues, ...PBTerm })
		} else if (!selectedPBTerm && !isNewBeingCreated.current) {
			reset({ ...PBTermInitialValues })
		}
	}, [selectedPBTerm, PBTerm])

	const handleSelected = (PBTermId: string) => {
		setSearchParams({ selected: PBTermId || "" })
		isNewBeingCreated.current = false
	}

	return (
		<>
			<GlobalSidebar>
				<SidebarList
					title={"PBTerms"}
					loading={loading}
					selected={selectedPBTerm}
					setSelected={handleSelected}
					list={sidebarPBTerms.sort(sortByProperty("name"))}
				/>
			</GlobalSidebar>

			<div className="space-y-5">
				<FormProvider {...methods}>
					<form onSubmit={handleSubmit(onSubmit)}>
						<div className="flex flex-col space-y-2">
							<div className="flex w-1/5 space-x-5">
								<FormInputAntd type="text" label="Name" name="name" />
							</div>

							<div className="flex w-2/5 space-x-5">
								<FormInputAntd type="textarea" label="Description" name="description" />
							</div>

							<div className="flex space-x-2">
								<AntdButton
									htmlType="button"
									onClick={() => {
										isNewBeingCreated.current = true
										reset({ ...PBTermInitialValues })
										setSearchParams({ selected: "" })
									}}
								>
									New
								</AntdButton>

								<AntdButton htmlType="submit" disabled={!isDirty || isSubmitting} loading={saving}>
									{selectedPBTerm ? "Update" : "Save"}
								</AntdButton>
							</div>
						</div>
					</form>
				</FormProvider>
				{/* <PBTermDetails termId={selected || ""} /> */}
			</div>
		</>
	)
}

export default PBTerms
